import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

const NAV_LINKS = [
  { label: "How It Works", to: "/how-it-works" },
  { label: "Capabilities",  to: "/capabilities"  },
  { label: "Marketplace",   to: "/find-work"     },
  { label: "Dashboard",     to: "/dashboard"     },
  { label: "Pricing",       to: "/pricing"       },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { user, logout, isLoggedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleLogout = () => {
    logout();
    setDropdownOpen(false);
    navigate("/");
  };

  const profileLink = user?.type === "employer" ? "/account/employer" : "/account/worker";
  const dashboardLink = user?.type === "employer" ? "/dashboard/employer" : "/dashboard";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 border-b ${
        scrolled ? "bg-white border-gray-200" : "bg-white/95 backdrop-blur-sm border-gray-150"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
        {/* Brand Logo */}
        <Link to="/" className="flex items-center flex-shrink-0 cursor-pointer overflow-hidden h-[72px]">
          <img
            src="https://static.readdy.ai/image/8b9b321cd7d670b6a60b0d1b6baf2111/e3a635c31b7543fa0ee8e8d84db9d640.png"
            alt="Gig n' Cash"
            className="h-[148px] w-auto object-contain translate-y-2"
          />
        </Link>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex items-center gap-0.5">
          {NAV_LINKS.map((link) => {
            const active = location.pathname === link.to;
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3.5 py-1.5 text-[13px] font-medium rounded-md transition-colors duration-150 whitespace-nowrap relative group ${
                  active ? "text-gray-900" : "text-gray-500 hover:text-gray-900"
                }`}
              >
                {link.label}
                <span
                  className={`absolute bottom-0.5 left-3.5 right-3.5 h-px bg-emerald-500 transition-transform duration-200 origin-left ${
                    active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                  }`}
                />
              </Link>
            );
          })}
        </div>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-2">
          {isLoggedIn && user ? (
            /* Logged-in user avatar + dropdown */
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <img src={user.avatar} alt={user.name} className="w-7 h-7 rounded-lg object-cover" />
                <span className="text-[13px] font-semibold text-gray-800 max-w-[100px] truncate">{user.name.split(" ")[0]}</span>
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${user.type === "worker" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-700"}`}>
                  {user.type === "worker" ? "Worker" : "Employer"}
                </span>
                <i className={`ri-arrow-down-s-line text-gray-400 text-sm transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
              </button>
              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-gray-100 rounded-xl py-1 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="text-sm font-bold text-gray-900 truncate">{user.name}</div>
                    <div className="text-xs text-gray-500 truncate">{user.email}</div>
                  </div>
                  <Link to={profileLink} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                    <i className="ri-user-line text-gray-400" />My Profile
                  </Link>
                  <Link to={dashboardLink} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                    <i className="ri-dashboard-line text-gray-400" />Dashboard
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer border-t border-gray-100 mt-1"
                  >
                    <i className="ri-logout-box-line" />Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* Logged-out: Sign In + Sign Up */
            <>
              <Link
                to="/login"
                className="px-4 py-1.5 text-[13px] font-semibold rounded-md border border-gray-300 text-gray-700 hover:border-emerald-500 hover:text-emerald-600 transition-colors duration-150 whitespace-nowrap"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                className="px-4 py-1.5 text-[13px] font-semibold rounded-md bg-emerald-500 text-white hover:bg-emerald-600 transition-colors duration-150 whitespace-nowrap"
              >
                Get Started
              </Link>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex items-center justify-center w-8 h-8 cursor-pointer text-gray-700"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-lg ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-0.5">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-left px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                  location.pathname === link.to
                    ? "text-emerald-700 bg-emerald-50"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
              >
                {link.label}
              </Link>
            ))}
            {isLoggedIn && user ? (
              <div className="flex flex-col gap-1 pt-3 border-t border-gray-100 mt-1">
                <div className="flex items-center gap-3 px-3 py-2">
                  <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-lg object-cover" />
                  <div>
                    <div className="text-sm font-bold text-gray-900">{user.name}</div>
                    <div className="text-xs text-gray-500">{user.type === "worker" ? "Worker" : "Employer"}</div>
                  </div>
                </div>
                <Link to={profileLink} className="px-3 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-md transition-colors">
                  My Profile
                </Link>
                <Link to={dashboardLink} className="px-3 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-md transition-colors">
                  Dashboard
                </Link>
                <button onClick={handleLogout} className="text-left px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors cursor-pointer">
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="flex gap-2 pt-3 border-t border-gray-100 mt-1">
                <Link
                  to="/login"
                  className="flex-1 py-2 text-sm font-semibold text-center rounded-md border border-gray-300 text-gray-700 hover:border-emerald-500 hover:text-emerald-600 transition-colors whitespace-nowrap"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="flex-1 py-2 text-sm font-semibold text-center rounded-md bg-emerald-500 text-white hover:bg-emerald-600 transition-colors whitespace-nowrap"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
